import dayjs from 'dayjs';

import type { Booking } from '../api/bookings';
import type { CashAccountBalance } from '../api/cash';
import type { CountChip, WorkSectionId } from './workList';

/**
 * Today as a dashboard (canvas "Today screen redesign", option A, owner 2026-10-26): the day in one
 * sentence on the band, four figure cards over it, the work list filtered by kind, where today's
 * money came in, and the garments going out over the next seven days. Every count is one the
 * screen already reads; nothing here asks the server anything.
 */

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "3 to hand over, 4 to take back, 2 late." — the band's line, from the count chips. */
export function daySentence(chips: readonly CountChip[]): string {
  const said = chips
    .filter((chip) => chip.id !== 'workroom' && chip.count > 0)
    .map((chip) => (chip.id === 'overdue' ? `${chip.count} late` : `${chip.count} ${chip.label}`));

  if (said.length === 0) {
    return chips.some((chip) => chip.id !== 'workroom')
      ? 'Nothing to hand over or take back today.'
      : '';
  }

  return `${said.join(', ')}.`;
}

/** One of the four figure cards over the band. */
export interface DayFigure {
  id: CountChip['id'];
  label: string;
  count: number;
  hint: string;
  /** Said in amber — something late. */
  attention: boolean;
}

const figureLabel: Record<CountChip['id'], string> = {
  pickups: 'Hand over today',
  returns: 'Take back today',
  overdue: 'Late',
  workroom: 'In the workroom',
};

/**
 * The count chips as the four cards: each its figure and a hint under it. The workroom's hint
 * splits its one figure into checking, tailor and cleaner, for whichever of them this person sees.
 */
export function dayFigures(
  chips: readonly CountChip[],
  visible: (id: string) => boolean,
  count: (id: string) => number | undefined,
): DayFigure[] {
  return chips.map((chip) => ({
    id: chip.id,
    label: figureLabel[chip.id],
    count: chip.count,
    attention: chip.attention,
    hint:
      chip.id === 'pickups'
        ? 'bookings going out today'
        : chip.id === 'returns'
          ? 'bookings coming back today'
          : chip.id === 'overdue'
            ? chip.count === 0
              ? 'nothing past its day'
              : 'past their return day'
            : [
                visible('inspection') ? `${count('inspection') ?? 0} to check` : null,
                visible('alterations') ? `${count('alterations') ?? 0} at the tailor` : null,
                visible('cleaning') ? `${count('cleaning') ?? 0} at the cleaner` : null,
              ]
                .filter((part): part is string => part !== null)
                .join(' · '),
  }));
}

/** The work list's filter: every section, or one kind of work. */
export type WorkFilter = 'all' | 'late' | 'counter-out' | 'counter-back' | 'workroom';

const filterOf: Record<WorkSectionId, Exclude<WorkFilter, 'all'>> = {
  overdue: 'late',
  pickups: 'counter-out',
  returns: 'counter-back',
  inspection: 'workroom',
  alterations: 'workroom',
  cleaning: 'workroom',
};

const filterLabel: Record<WorkFilter, string> = {
  all: 'All',
  late: 'Late',
  'counter-out': 'Hand over',
  'counter-back': 'Take back',
  workroom: 'Workroom',
};

/** Whether a section shows under a filter. */
export function inFilter(id: WorkSectionId, filter: WorkFilter): boolean {
  return filter === 'all' || filterOf[id] === filter;
}

/**
 * The filter chips over the work list, each with its count — only kinds that have work, and All
 * only when more than one kind does.
 */
export function workFilters(
  sections: readonly { id: WorkSectionId; count: number }[],
): { id: WorkFilter; label: string; count: number }[] {
  const kinds = (['late', 'counter-out', 'counter-back', 'workroom'] as const)
    .map((id) => ({
      id,
      label: filterLabel[id],
      count: sections
        .filter((section) => filterOf[section.id] === id)
        .reduce((sum, section) => sum + section.count, 0),
    }))
    .filter((kind) => kind.count > 0);
  const total = kinds.reduce((sum, kind) => sum + kind.count, 0);

  return kinds.length > 1 ? [{ id: 'all', label: filterLabel.all, count: total }, ...kinds] : kinds;
}

/**
 * Where today's money came in, account by account — the till, the bank — each with its share of
 * everything that came in. Accounts that took nothing today are left out.
 */
export function moneyInByAccount(
  accounts: readonly Pick<CashAccountBalance, 'id' | 'name' | 'in'>[],
): { id: number; name: string; amount: number; share: number }[] {
  const total = accounts.reduce((sum, account) => sum + Math.max(0, account.in), 0);

  return accounts
    .filter((account) => account.in > 0)
    .sort((a, b) => b.in - a.in)
    .map((account) => ({
      id: account.id,
      name: account.name,
      amount: account.in,
      share: total === 0 ? 0 : Math.round((account.in / total) * 100),
    }));
}

/** One day of the week ahead. */
export interface AheadDay {
  date: string;
  /** "Tue". */
  day: string;
  garments: number;
}

/**
 * Garments going out on each of the next `days` days after `today`, from the bookings picked up in
 * that window, each counted by the server's own `garmentCount`. Days with nothing still appear, at
 * zero, so the bars keep their places.
 */
export function weekAhead(
  bookings: readonly Pick<Booking, 'possessionFromDate' | 'garmentCount'>[],
  today: string,
  days = 7,
): AheadDay[] {
  const start = dayjs(today.slice(0, 10));

  return Array.from({ length: days }, (_, index) => {
    const date = start.add(index + 1, 'day');
    const iso = date.format('YYYY-MM-DD');

    return {
      date: iso,
      day: date.format('ddd'),
      garments: bookings
        .filter((booking) => booking.possessionFromDate.slice(0, 10) === iso)
        .reduce((sum, booking) => sum + booking.garmentCount, 0),
    };
  });
}

/** The week ahead's busiest day in a sentence, or nothing when nothing goes out. */
export function busiestLine(week: readonly AheadDay[]): string | null {
  const busiest = week.reduce<AheadDay | null>(
    (best, day) => (day.garments > (best?.garments ?? 0) ? day : best),
    null,
  );

  return busiest
    ? `${dayjs(busiest.date).format('ddd D MMM')} is the busiest — ${plural(busiest.garments, 'garment')} out.`
    : null;
}
